/**
 * Register grid — row ownership (AVA-602, showcase B "register-table-ownership-showcase.html").
 *
 * Pure rules for the central Facility Group view: the `facilitySummary` the table-data API returns
 * for a Separated register, each row's owning Facility, the Facility filter and its query params,
 * and the one-line scope hint shown for Shared / member Separated registers.
 *
 * No Vue / Vuetify / `@/` imports: unit-tested with `node --test`.
 */

export const FACILITY_ASSIGNMENT_MODE = Object.freeze({ SHARED: 'Shared', SEPARATED: 'Separated' });

export const FACILITY_FILTER_KIND = Object.freeze({
  ALL: 'all',
  FACILITY: 'facility',
  UNASSIGNED: 'unassigned',
});

export const ALL_FACILITIES_FILTER = Object.freeze({ kind: FACILITY_FILTER_KIND.ALL, facilityId: null });

/** Shown when a row points at a Facility whose name the response did not carry. */
export const UNKNOWN_FACILITY_NAME = 'Unknown Facility';

const numberFormat = new Intl.NumberFormat('en-US');

export function formatCount(value) {
  return numberFormat.format(Number.isFinite(value) ? value : 0);
}

function plural(count, one, many) {
  return `${formatCount(count)} ${count === 1 ? one : many}`;
}

function nonNegativeInteger(value) {
  const number = Number(value);
  return Number.isInteger(number) && number >= 0 ? number : 0;
}

function nonEmptyString(value) {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  return text || null;
}

/**
 * Normalises the table-data `facilitySummary` (central Separated reads only; `null` otherwise).
 * Anything malformed collapses to `null` so the UI simply hides the chips and the filter.
 * @param {unknown} raw `{ totalRowCount, unassignedRowCount, facilities: [{ facilityId, name, rowCount }] }`
 * @returns {null | { totalRowCount: number, unassignedRowCount: number,
 *   facilities: Array<{ facilityId: string, name: string, rowCount: number }> }}
 */
export function normalizeFacilitySummary(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const facilities = (Array.isArray(raw.facilities) ? raw.facilities : [])
    .map((facility) => {
      const facilityId = nonEmptyString(facility?.facilityId);
      if (!facilityId) return null;
      return {
        facilityId,
        name: nonEmptyString(facility?.name) || UNKNOWN_FACILITY_NAME,
        rowCount: nonNegativeInteger(facility?.rowCount),
      };
    })
    .filter(Boolean);
  return {
    totalRowCount: nonNegativeInteger(raw.totalRowCount),
    unassignedRowCount: nonNegativeInteger(raw.unassignedRowCount),
    facilities,
  };
}

/** The row's owning Facility id, or `null` when the row has none (API keys are PascalCase or camelCase). */
export function rowFacilityId(row) {
  if (!row || typeof row !== 'object') return null;
  return nonEmptyString(row.FacilityId) || nonEmptyString(row.facilityId);
}

/**
 * Who owns a row, for the Facility column and the row details panel.
 * Name lookup order: the row's own name -> the summary -> the app's Facility dictionary.
 * @returns {{ facilityId: string|null, name: string|null, unassigned: boolean }}
 */
export function resolveRowFacility(row, { summary = null, facilityDict = null } = {}) {
  const facilityId = rowFacilityId(row);
  if (!facilityId) return { facilityId: null, name: null, unassigned: true };
  const key = facilityId.toLowerCase();
  const fromSummary = (summary?.facilities || [])
    .find((facility) => facility.facilityId.toLowerCase() === key)?.name;
  const fromDict = facilityDict && typeof facilityDict === 'object'
    ? nonEmptyString(facilityDict[facilityId])
    : null;
  const name = nonEmptyString(row.FacilityName) || nonEmptyString(row.facilityName)
    || fromSummary || fromDict || UNKNOWN_FACILITY_NAME;
  return { facilityId, name, unassigned: false };
}

/** Normalises any filter value to one of the three shapes; unknown input means "All". */
export function normalizeFacilityFilter(filter) {
  if (filter?.kind === FACILITY_FILTER_KIND.UNASSIGNED) {
    return { kind: FACILITY_FILTER_KIND.UNASSIGNED, facilityId: null };
  }
  const facilityId = nonEmptyString(filter?.facilityId);
  if (filter?.kind === FACILITY_FILTER_KIND.FACILITY && facilityId) {
    return { kind: FACILITY_FILTER_KIND.FACILITY, facilityId };
  }
  return { ...ALL_FACILITIES_FILTER };
}

export function isFacilityFilterActive(filter) {
  return normalizeFacilityFilter(filter).kind !== FACILITY_FILTER_KIND.ALL;
}

export function sameFacilityFilter(left, right) {
  const a = normalizeFacilityFilter(left);
  const b = normalizeFacilityFilter(right);
  return a.kind === b.kind && String(a.facilityId || '').toLowerCase() === String(b.facilityId || '').toLowerCase();
}

/**
 * table-data query params for the Facility filter (the only place this contract lives).
 *   All          -> no params (every row, assigned or not)
 *   One Facility -> `facilityIds=<id>` and `includeUnassigned=false`
 *   Unassigned   -> `includeUnassigned=true` with no `facilityIds` (rows with no Facility only)
 * `facilityIds` is comma-separated so a multi-Facility filter can reuse it later.
 */
export function facilityFilterQueryParams(filter) {
  const normalized = normalizeFacilityFilter(filter);
  if (normalized.kind === FACILITY_FILTER_KIND.FACILITY) {
    return { facilityIds: normalized.facilityId, includeUnassigned: false };
  }
  if (normalized.kind === FACILITY_FILTER_KIND.UNASSIGNED) return { includeUnassigned: true };
  return {};
}

/**
 * Query params (or body fields) that say WHERE a register read comes from: the table-data
 * `viewContext` / `facilityId` pair. Every read behind the table-data admission needs it
 * (table-data, export-advanced, column-values - API a4929ece): the API treats a request without
 * `viewContext` as central and admits a member view only with an explicit `facilityId`.
 * Unknown keys and empty values are dropped.
 * @param {{ viewContext?: string|null, facilityId?: string|null }} scope
 * @returns {{ viewContext?: string, facilityId?: string }}
 */
export function registerReadScopeFields(scope) {
  const fields = {};
  const viewContext = nonEmptyString(scope?.viewContext);
  if (viewContext) fields.viewContext = viewContext;
  const facilityId = nonEmptyString(scope?.facilityId);
  if (facilityId) fields.facilityId = facilityId;
  return fields;
}

/**
 * export-advanced body fields that say WHERE the export reads from (every export mode): the
 * table-data read scope (`registerReadScopeFields`).
 * @param {{ viewContext?: string|null, facilityId?: string|null }} scope
 */
export function exportReadScopeFields(scope) {
  return registerReadScopeFields(scope);
}

/**
 * export-advanced body fields for a Filtered export: the read scope (`exportReadScopeFields`) plus the
 * central Separated Facility filter - the same `facilityIds` / `includeUnassigned` contract as table-data.
 * The filter fields only ever come from the central view (`facilityFilterQueryParams` is empty elsewhere).
 * Unknown keys and empty values are dropped.
 * @param {{ viewContext?: string|null, facilityId?: string|null, facilityIds?: string,
 *   includeUnassigned?: boolean }} scope
 */
export function facilityExportFields(scope) {
  const fields = exportReadScopeFields(scope);
  const facilityIds = nonEmptyString(scope?.facilityIds);
  if (facilityIds) fields.facilityIds = facilityIds;
  if (typeof scope?.includeUnassigned === 'boolean') fields.includeUnassigned = scope.includeUnassigned;
  return fields;
}

/** True when the export scope narrows rows by Facility (a Facility, or Unassigned). */
export function isFacilityExportFiltered(scope) {
  const fields = facilityExportFields(scope);
  return Boolean(fields.facilityIds) || fields.includeUnassigned === true;
}

/** Menu items: All, each Facility with its row count, then Unassigned (only when it has rows or is selected). */
export function facilityFilterItems(summary, activeFilter = ALL_FACILITIES_FILTER) {
  if (!summary) return [];
  const active = normalizeFacilityFilter(activeFilter);
  const items = [{
    key: 'all',
    filter: { ...ALL_FACILITIES_FILTER },
    label: 'All Facilities',
    count: summary.totalRowCount,
    warning: false,
  }];
  summary.facilities.forEach((facility) => {
    items.push({
      key: `facility:${facility.facilityId}`,
      filter: { kind: FACILITY_FILTER_KIND.FACILITY, facilityId: facility.facilityId },
      label: facility.name,
      count: facility.rowCount,
      warning: false,
    });
  });
  if (summary.unassignedRowCount > 0 || active.kind === FACILITY_FILTER_KIND.UNASSIGNED) {
    items.push({
      key: 'unassigned',
      filter: { kind: FACILITY_FILTER_KIND.UNASSIGNED, facilityId: null },
      label: 'Unassigned',
      count: summary.unassignedRowCount,
      warning: true,
    });
  }
  return items.map((item) => ({ ...item, selected: sameFacilityFilter(item.filter, active) }));
}

/** Label for the toolbar's Facility filter button. */
export function facilityFilterLabel(filter, summary) {
  const normalized = normalizeFacilityFilter(filter);
  if (normalized.kind === FACILITY_FILTER_KIND.UNASSIGNED) return 'Unassigned';
  if (normalized.kind === FACILITY_FILTER_KIND.FACILITY) {
    const key = normalized.facilityId.toLowerCase();
    return (summary?.facilities || []).find((facility) => facility.facilityId.toLowerCase() === key)?.name
      || UNKNOWN_FACILITY_NAME;
  }
  return 'All Facilities';
}

/** Header summary next to the row count: "3 Facilities" and the amber "12 unassigned" shortcut. */
export function facilitySummaryChips(summary) {
  if (!summary) return null;
  return {
    facilitiesLabel: plural(summary.facilities.length, 'Facility', 'Facilities'),
    unassignedCount: summary.unassignedRowCount,
    unassignedLabel: `${formatCount(summary.unassignedRowCount)} unassigned`,
  };
}

/**
 * Small scope hint in the toolbar (showcase `scopeHint`). `null` when nothing should show.
 * @param {{ viewContext?: string|null, mode?: string|null, groupName?: string|null,
 *   facilityName?: string|null, memberFacilityCount?: number|null }} context
 */
export function registerScopeHint({
  viewContext = null,
  mode = null,
  groupName = null,
  facilityName = null,
  memberFacilityCount = null,
} = {}) {
  if (mode === FACILITY_ASSIGNMENT_MODE.SHARED && viewContext === 'central') {
    const count = Number.isInteger(memberFacilityCount) && memberFacilityCount > 0 ? memberFacilityCount : null;
    return {
      icon: 'mdi-domain',
      text: count
        ? `Facility Group level · all ${plural(count, 'Facility', 'Facilities')} see every row`
        : 'Facility Group level · every member Facility sees every row',
      title: 'Rows have no Facility. Every member Facility sees every row.',
    };
  }
  if (mode === FACILITY_ASSIGNMENT_MODE.SHARED && viewContext === 'member') {
    const group = nonEmptyString(groupName);
    return {
      icon: 'mdi-domain',
      text: group ? `Shared across ${group}` : 'Shared across the Facility Group',
      title: 'Every member Facility sees every row.',
    };
  }
  if (mode === FACILITY_ASSIGNMENT_MODE.SEPARATED && viewContext === 'member') {
    const facility = nonEmptyString(facilityName);
    return {
      icon: 'mdi-map-marker-outline',
      text: facility ? `${facility} rows only` : "This Facility's rows only",
      title: 'Each row belongs to one Facility. You see only this Facility\'s rows.',
    };
  }
  return null;
}
